/* eslint-disable react-refresh/only-export-components */
import { Suspense, useEffect } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";

import { AuthProvider } from "@/providers/auth";
import { FeatureFlagsProvider } from "@/providers/featureFlags";
import { ProtectedRouteLayout } from "@/layouts/ProtectedRouteLayout";
import { Spinner } from "@/components/ui/Spinner";

import { DemoKitRestrictedPageRedirect } from "@/features/enroll/onboarding/components/DemoKitRestrictedPageRedirect";

import { user } from "@/utils/firebase/user";
import { lazyImport } from "@/lazy-import";
import { ROUTES } from "@/constants/routes";

const { AssetsRoutes } = lazyImport(
  () => import("../features/assets"),
  "AssetsRoutes",
);
const { TripsRoutes } = lazyImport(
  () => import("../features/trips"),
  "TripsRoutes",
);
const { AlertsRoutes } = lazyImport(
  () => import("../features/alerts"),
  "AlertsRoutes",
);
const { SupportRoutes } = lazyImport(
  () => import("../features/support"),
  "SupportRoutes",
);
const { SystemsRoutes } = lazyImport(
  () => import("../features/system"),
  "SystemsRoutes",
);
const { ReportsRoutes } = lazyImport(
  () => import("../features/reports"),
  "ReportsRoutes",
);
const { TowersRoutes } = lazyImport(
  () => import("../features/towers"),
  "TowersRoutes",
);

const { MainRoutes } = lazyImport(
  () => import("../features/main"),
  "MainRoutes",
);

const { SensorLocationDashboardRoutes } = lazyImport(
  () => import("../features/sensor-location-dashboard"),
  "SensorLocationDashboardRoutes",
);

const { DemoKitOnboardingRedirect } = lazyImport(
  () => import("../features/enroll/onboarding"),
  "DemoKitOnboardingRedirect",
);

const { DemoKitOnboardingRoutes } = lazyImport(
  () => import("../features/enroll/onboarding"),
  "DemoKitOnboardingRoutes",
);

const App = () => {
  const navigate = useNavigate();
  useEffect(() => {
    return user.auth.onAuthStateChanged((user) => {
      if (user) {
        return;
      }
      navigate(ROUTES.LOGIN, { replace: true });
    });
  }, [navigate]);

  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <ProtectedRouteLayout>
          <Suspense fallback={null}>
            <DemoKitOnboardingRedirect />
          </Suspense>
          <Suspense
            fallback={
              <div
                className="-mt-8 flex h-dvh w-full items-center justify-center"
                data-testid={"app-loading"}
              >
                <Spinner size="lg" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </ProtectedRouteLayout>
      </FeatureFlagsProvider>
    </AuthProvider>
  );
};

export const protectedRoutes = [
  {
    path: "/app/*",
    element: <App />,
    children: [
      {
        path: "assets/*",
        element: (
          <DemoKitRestrictedPageRedirect>
            <AssetsRoutes />
          </DemoKitRestrictedPageRedirect>
        ),
      },
      { path: "trips/*", element: <TripsRoutes /> },
      { path: "alerts/*", element: <AlertsRoutes /> },
      { path: "support/*", element: <SupportRoutes /> },
      { path: "system/*", element: <SystemsRoutes /> },
      {
        path: "reports/*",
        element: (
          <DemoKitRestrictedPageRedirect>
            <ReportsRoutes />
          </DemoKitRestrictedPageRedirect>
        ),
      },
      { path: "towers/*", element: <TowersRoutes /> },
      {
        path: "admin/sensor-location-dashboard/*",
        element: <SensorLocationDashboardRoutes />,
      },
      {
        path: "demo-kit-onboarding",
        element: <DemoKitOnboardingRoutes />,
      },
      { path: "", element: <MainRoutes /> },
      { path: "*", element: <Navigate to=".." /> },
    ],
  },
];
